import { getLocale } from '@rematter/common/locale';
import { CurrencyCode } from '@rematter/frontend-lib-api-client';
import {
  Col,
  ConsoleTouchDialog,
  Grid,
  Money,
  Row,
  TouchButton,
} from '@rematter/ui-web';
import { formatMoney, humanReadableWeight } from '@rematter/ui-web/lib';
import { LucideShip, LucideWeight } from 'lucide-react';

import { LoadRecord } from '../../../../types/load';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { useTheme, useUIPreferences } from '~/shared/hooks';

type ShipTicketDialogProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  load: LoadRecord;
  onConfirm: () => void;
  isProcessing: boolean;
  customerName?: string | null;
  subtotal?: { amount: number; currencyCode: CurrencyCode } | null;
  deductionsTotal?: { amount: number; currencyCode: CurrencyCode } | null;
  grandTotal?: { amount: number; currencyCode: CurrencyCode } | null;
};

//
// ⚛️ ⚛️ ⚛️ SHIP TICKET DIALOG ⚛️ ⚛️ ⚛️
//
export const ShipTicketDialog = ({
  isOpen,
  onOpenChange,
  load,
  onConfirm,
  isProcessing,
  customerName,
  subtotal,
  deductionsTotal,
  grandTotal,
}: ShipTicketDialogProps) => {
  const t = useTranslations();

  // Other data hooks
  const { effectiveColorScheme } = useTheme();
  const { touchModeEnabled } = useUIPreferences();

  const ts = t.scaleTicketPro.shipLoad;

  return (
    <ConsoleTouchDialog
      touchModeEnabled={touchModeEnabled}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@ShipTicketDialog"
      dialogClassName="max-h-[80vh] w-[90vw] max-w-[500px] p-0"
      header={
        <Row gap3 yCenter>
          <Col xCenter yCenter className="h-10 w-10 rounded-full bg-primary/10">
            <LucideShip className="h-5 w-5 text-primary" />
          </Col>
          <Col>
            <div className="text-base font-semibold">{ts.title}</div>
            <div className="text-sm text-neutral/60">
              {ts.subtitle(String(load.loadNumber))}
            </div>
          </Col>
        </Row>
      }
      footer={
        <Row xEnd gap2 className="w-full [&>*]:min-w-30">
          <TouchButton
            variant="outline"
            color="neutral"
            onPress={() => onOpenChange(false)}
            isDisabled={isProcessing}
            className="border-2"
          >
            {t.common.cancel}
          </TouchButton>
          <TouchButton
            variant="solid"
            color="primary"
            onPress={onConfirm}
            isDisabled={isProcessing}
            className="border-2"
          >
            {isProcessing ? ts.processingButton : ts.confirmButton}
          </TouchButton>
        </Row>
      }
    >
      <Col gap4>
        <Col gap2 className="rounded-lg bg-neutral/5 p-4">
          <div className="text-sm font-medium text-neutral/60">
            {ts.shipmentSummary}
          </div>

          {customerName && (
            <Row className="justify-between">
              <span className="text-neutral/60">{ts.customer}</span>
              <span className="font-medium">{customerName}</span>
            </Row>
          )}

          {load.salesOrderNumber && (
            <Row className="justify-between">
              <span className="text-neutral/60">{ts.salesOrder}</span>
              <span className="font-medium">#{load.salesOrderNumber}</span>
            </Row>
          )}

          <Row className="justify-between">
            <span className="text-neutral/60">{ts.lineItems}</span>
            <span className="font-medium">{load.lineItems?.length ?? 0}</span>
          </Row>

          <Row xBetween>
            <div className="text-neutral/60">{ts.subtotal}</div>
            <div className="font-medium">
              <Money {...subtotal} />
            </div>
          </Row>

          <Row xBetween>
            <div className="text-neutral/60">{ts.deductions}</div>
            <div className="font-medium text-failure">
              -
              <Money {...deductionsTotal} />
            </div>
          </Row>

          {grandTotal && (
            <Row className="justify-between border-t pt-2">
              <span className="font-medium">{ts.grandTotal}</span>
              <span className="text-lg font-semibold">
                {formatMoney(grandTotal.amount, grandTotal.currencyCode)}
              </span>
            </Row>
          )}
        </Col>

        <Grid cols2 gap4>
          <Col className="rounded-lg border p-3">
            <div className="text-xs text-neutral/60">{ts.heavyWeight}</div>
            <Row gap1 yCenter className="font-medium">
              <LucideWeight className="h-4 w-4 text-neutral/60" />
              {load.heavyWeight?.value
                ? humanReadableWeight(load.heavyWeight?.value, {
                    unit: load.heavyWeight?.unit,
                    decimalPlaces: 2,
                    includeUnit: false,
                    locale: getLocale(),
                  })
                : null}
            </Row>
          </Col>
          <Col className="rounded-lg border p-3">
            <div className="text-xs text-neutral/60">{ts.lightWeight}</div>
            <Row gap1 yCenter className="font-medium">
              <LucideWeight className="h-4 w-4 text-neutral/60" />
              {humanReadableWeight(load.lightWeight?.value ?? 0, {
                unit: load.lightWeight?.unit,
                decimalPlaces: 2,
                includeUnit: false,
                locale: getLocale(),
              })}
            </Row>
          </Col>
        </Grid>

        <p className="text-sm text-neutral/60">{ts.description}</p>
      </Col>
    </ConsoleTouchDialog>
  );
};
